import { API_BASE_URL, AUTH_HEADER_PREFIX } from '../config';
// OAuth2/JWT Refresh Token Flow Implementation
// ============================================
// 1. Client sends request with expired access token → Gets 401/403 error
// 2. Client calls refresh endpoint (POST /api/v1/auth/refresh-token) with refresh token cookie
// 3. Server validates the refresh token (checks signature, expiry, database record)
// 4. Server generates a new access token (valid for 15 minutes)
// 5. Server optionally rotates the refresh token (enabled)
// 6. Client receives new access token and continues making API calls
//
// This keeps users logged in seamlessly without re-authentication.
// Refresh happens ONLY reactively on 401/403 errors (no proactive refresh).

// Note: refresh token cookie is HttpOnly and not accessible to JS.
// The client uses credentials: 'include' to let the browser send the cookie automatically.

// Implements steps 2-6 of the OAuth2 refresh flow above.

// ==============================|| CONSTANTS ||============================== //

const CONTENT_TYPE_JSON = 'application/json';
const ERROR_MESSAGES = {
  NO_ACCESS_TOKEN: 'No access token received from refresh endpoint.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.'
};

const refreshAccessToken = async () => {
  // Step 2: Call refresh endpoint with HttpOnly refresh token cookie
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': CONTENT_TYPE_JSON
    },
    credentials: 'include', // Browser automatically sends HttpOnly refresh token cookie
    body: JSON.stringify({}) // Empty body - server reads refresh token from cookie
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Steps 4-6: Server validated refresh token and generated new access token
  if (data.data?.accessToken) {
    // Store new access token (valid for 15 minutes)
    localStorage.setItem('token', data.data.accessToken);

    // Update user data if server provides it
    if (data.data?.user) {
      localStorage.setItem('userId', data.data.user.id);
      localStorage.setItem('userEmail', data.data.user.email);
      localStorage.setItem('userRole', data.data.user.role);
    }

    return data.data.accessToken;
  }

  throw new Error(ERROR_MESSAGES.NO_ACCESS_TOKEN);
};

// Custom fetch-based API client with better cookie support
const createApiClient = () => {
  const baseURL = `${API_BASE_URL}/api/v1`; // Updated to match backend v1 structure

  const buildRequestConfig = (options, token) => {
    const config = {
      credentials: 'include', // Always include cookies
      headers: {
        ...options.headers
      },
      ...options
    };

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    if (token) {
      config.headers.Authorization = `${AUTH_HEADER_PREFIX}${token}`;
    }

    return config;
  };

  const handleAuthError = async (endpoint, options, config, isRetry) => {
    if (isRetry || endpoint.includes('/auth/')) {
      return null; // Don't retry auth endpoints or already retried requests
    }

    try {
      const newToken = await refreshAccessToken();
      const newConfig = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${newToken}`
        }
      };
      return makeRequest(endpoint, { ...options, headers: newConfig.headers }, true);
    } catch (refreshError) {
      console.error('❌ Token refresh failed:', refreshError);
      throw refreshError;
    }
  };

  const parseErrorResponse = async (response) => {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If we can't parse JSON, use the default error message
    }
    return errorMessage;
  };

  const parseSuccessResponse = async (response) => {
    const contentType = response.headers.get('Content-Type') || '';
    const headers = {};
    for (const [key, value] of response.headers.entries()) headers[key] = value;

    if (contentType.includes('application/json')) {
      const data = await response.json();
      return { data, status: response.status, headers };
    }

    // For CSV or any other non-JSON content
    const blob = await response.blob();
    return { data: blob, status: response.status, headers };
  };

  const makeRequest = async (endpoint, options = {}, isRetry = false) => {
    const url = `${baseURL}${endpoint}`;
    const token = localStorage.getItem('token');
    const config = buildRequestConfig(options, token);

    try {
      const response = await fetch(url, config);

      // Handle 401/403 responses - Step 1: Client got 401/403 with expired/invalid access token
      if (response.status === 401 || response.status === 403) {
        const retryResult = await handleAuthError(endpoint, options, config, isRetry);
        if (retryResult) {
          return retryResult;
        }
      }

      // Handle other non-ok responses
      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      // Parse successful response
      return await parseSuccessResponse(response);
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      throw error;
    }
  };

  const request = (endpoint, options = {}) => makeRequest(endpoint, options, false);

  // Helper function to build query string from parameters
  const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          for (const v of value) {
            searchParams.append(key, v);
          }
        } else {
          searchParams.append(key, value);
        }
      }
    }
    return searchParams.toString();
  };

  return {
    get: (endpoint, options = {}) => {
      // Handle query parameters for GET requests
      let url = endpoint;
      if (options.params) {
        const queryString = buildQueryString(options.params);
        if (queryString) {
          url += `?${queryString}`;
        }
      }
      return request(url, { method: 'GET', ...options });
    },
    post: (endpoint, data, options = {}) =>
      request(endpoint, {
        method: 'POST',
        body: data instanceof FormData ? data : JSON.stringify(data),
        ...options
      }),
    put: (endpoint, data, options = {}) =>
      request(endpoint, {
        method: 'PUT',
        body: data instanceof FormData ? data : JSON.stringify(data),
        ...options
      }),
    delete: (endpoint, options = {}) => request(endpoint, { method: 'DELETE', ...options })
  };
};

const api = createApiClient();

export default api;
